import type { ChangeEvent, FocusEvent } from 'react'
import { useEffect, useState } from 'react'
import { useControlField, useField } from 'remix-validated-form'
import { FieldContainer } from '~/components'
import { Input } from '~/components/ui'
import { cn } from '~/utils'

type HeightFieldProps = {
  className?: string
  label?: string
  name: string
  required?: boolean
  disabled?: boolean
  onBlur?: (e: FocusEvent<HTMLInputElement, Element>) => void
}

export default function HeightField(props: HeightFieldProps) {
  const { name, disabled, onBlur: customOnBlur } = props
  const { error, validate, defaultValue, getInputProps } = useField(name)
  const { onBlur } = getInputProps()
  const [value, setValue] = useControlField<number | undefined>(name)

  const defaultFeet = defaultValue ? Math.floor(defaultValue) : undefined
  const defaultInches = defaultValue
    ? Math.round((defaultValue - Math.floor(defaultValue)) * 12)
    : undefined
  const [feet, setFeet] = useState<number | undefined>(defaultFeet)
  const [inches, setInches] = useState<number | undefined>(defaultInches)

  const feetClasses = cn('pr-7', {
    'ring-2 ring-yellow-300 ring-offset-2':
      defaultValue !== undefined && feet !== undefined && feet !== defaultFeet,
    'ring-2 ring-green-400 ring-offset-2':
      defaultValue === undefined && feet !== undefined,
  })

  const inchesClasses = cn('pr-7', {
    'ring-2 ring-yellow-300 ring-offset-2':
      defaultValue !== undefined &&
      inches !== undefined &&
      inches !== defaultInches,
    'ring-2 ring-green-400 ring-offset-2':
      defaultValue === undefined && inches !== undefined,
  })

  useEffect(() => {
    if (feet !== undefined && inches !== undefined) {
      const totalInches = feet * 12 + inches
      const decimalValue = totalInches / 12

      setValue(decimalValue)
    } else {
      setValue(undefined)
    }
  }, [feet, inches])

  function handleFeetChange(e: ChangeEvent<HTMLInputElement>) {
    const value = parseInt(e.target.value, 10)
    setFeet(isNaN(value) ? undefined : value)
  }

  function handleInchesChange(e: ChangeEvent<HTMLInputElement>) {
    const value = parseInt(e.target.value, 10)
    setInches(isNaN(value) ? undefined : Math.min(value, 11))
  }

  return (
    <FieldContainer {...props} id={name}>
      <div className="flex gap-4">
        <input type="hidden" name={name} value={value || ''} />
        <div className="relative flex-1 ">
          <Input
            className={feetClasses}
            value={feet ?? ''}
            onChange={handleFeetChange}
            disabled={disabled}
            min={0}
            onBlur={(e) => {
              onBlur?.(e)
              customOnBlur?.(e)
              validate()
            }}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground sm:text-sm">
            ft
          </span>
        </div>
        <div className="relative flex-1">
          <Input
            className={inchesClasses}
            value={inches ?? ''}
            onChange={handleInchesChange}
            disabled={disabled}
            min={0}
            max={11}
            onBlur={(e) => {
              onBlur?.(e)
              customOnBlur?.(e)
              validate()
            }}
          />
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground sm:text-sm">
            in
          </span>
        </div>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </FieldContainer>
  )
}
